import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';

const CustomSelect = ({
    value,
    onChange,
    options = [],
    valueKey = 'value',
    labelKey = 'label',
    label,
    labelClassName = '',
    placeholder = 'Select an option',
    disabled = false,
    required = false,
    className = '',
    buttonClassName = '',
    icon: IconComponent,
    searchable = false,
    searchPlaceholder = 'Search...',
    direction = 'auto'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [popoverStyle, setPopoverStyle] = useState({
        top: 0,
        left: 0,
        width: 0,
        dropUp: false
    });

    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);
    const selectedItemRef = useRef(null);

    const updatePosition = useCallback(() => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const estimatedMenuHeight = Math.min((options?.length || 5) * 40 + (searchable ? 45 : 10), 230);
            const shouldDropUp = direction === 'up' || (direction !== 'down' && spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow);

            setPopoverStyle({
                top: shouldDropUp ? rect.top - 6 : rect.bottom + 6,
                left: rect.left,
                width: rect.width,
                dropUp: shouldDropUp
            });
        }
    }, [direction, options?.length, searchable]);

    const handleToggle = () => {
        if (!isOpen) {
            updatePosition();
        }
        setIsOpen((prev) => !prev);
    };

    // Position updates and outside click handler
    useEffect(() => {
        if (!isOpen) return;

        updatePosition();

        const handleScrollOrResize = (e) => {
            if (dropdownRef.current && dropdownRef.current.contains(e.target)) return;
            updatePosition();
        };

        const handleOutsideClick = (e) => {
            if (
                buttonRef.current &&
                !buttonRef.current.contains(e.target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target)
            ) {
                setIsOpen(false);
            }
        };

        window.addEventListener('scroll', handleScrollOrResize, true);
        window.addEventListener('resize', handleScrollOrResize);
        document.addEventListener('mousedown', handleOutsideClick);

        return () => {
            window.removeEventListener('scroll', handleScrollOrResize, true);
            window.removeEventListener('resize', handleScrollOrResize);
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [isOpen, updatePosition]);

    // Scroll selected item into view when dropdown opens
    useEffect(() => {
        if (isOpen && selectedItemRef.current) {
            setTimeout(() => {
                selectedItemRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }, 50);
        }
    }, [isOpen]);

    const getOptionValue = (opt) => {
        if (opt === null || opt === undefined) return '';
        if (typeof opt === 'object') return opt[valueKey] !== undefined ? opt[valueKey] : opt[labelKey];
        return opt;
    };

    const getOptionLabel = (opt) => {
        if (opt === null || opt === undefined) return '';
        if (typeof opt === 'object') return opt[labelKey] !== undefined ? opt[labelKey] : opt[valueKey];
        return opt;
    };

    const selectedOption = options.find((opt) => String(getOptionValue(opt)) === String(value));
    const displayLabel = selectedOption ? getOptionLabel(selectedOption) : (value || '');

    const filteredOptions = options.filter((opt) => {
        if (!searchable || !searchQuery) return true;
        const text = String(getOptionLabel(opt)).toLowerCase();
        return text.includes(searchQuery.toLowerCase());
    });

    const handleSelect = (opt) => {
        const val = getOptionValue(opt);
        if (onChange) {
            onChange(val, opt);
        }
        setIsOpen(false);
        setSearchQuery('');
    };

    return (
        <div className={`space-y-1.5 ${className}`}>
            {label && (
                <label className={`block text-xs mb-1.5 ${labelClassName || 'font-medium text-slate-600'}`}>
                    {typeof label === 'string' && label.endsWith('*') ? (
                        <>
                            {label.slice(0, -1).trim()}{' '}
                            <span className="text-rose-500 ml-0.5">*</span>
                        </>
                    ) : (
                        <>
                            {label}
                            {required && <span className="text-rose-500 ml-0.5">*</span>}
                        </>
                    )}
                </label>
            )}

            <div className="relative">
                <button
                    ref={buttonRef}
                    type="button"
                    disabled={disabled}
                    onClick={handleToggle}
                    className={`w-full px-4 text-xs sm:text-sm h-[42px] rounded-xl border font-medium flex items-center justify-between transition-all bg-white cursor-pointer ${
                        isOpen
                            ? 'border-[#067978] ring-2 ring-[#067978]/20 text-slate-800 shadow-sm'
                            : 'border-slate-200 text-slate-700 hover:border-slate-300'
                    } ${disabled ? 'bg-slate-50 cursor-not-allowed opacity-75' : ''} ${buttonClassName}`}
                >
                    <div className="flex items-center gap-2 truncate">
                        {IconComponent && <IconComponent size={16} className={displayLabel ? 'text-[#067978]' : 'text-slate-400'} />}
                        <span className={displayLabel ? 'text-slate-700 font-medium' : 'text-slate-400 font-normal'}>
                            {displayLabel || placeholder}
                        </span>
                    </div>
                    <ChevronDown
                        size={16}
                        className={`text-slate-400 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180 text-[#067978]' : ''}`}
                    />
                </button>

                {isOpen && !disabled && typeof document !== 'undefined' && createPortal(
                    <div
                        ref={dropdownRef}
                        style={{
                            position: 'fixed',
                            left: `${popoverStyle.left}px`,
                            width: `${popoverStyle.width}px`,
                            top: popoverStyle.dropUp ? 'auto' : `${popoverStyle.top}px`,
                            bottom: popoverStyle.dropUp ? `${window.innerHeight - popoverStyle.top}px` : 'auto',
                            zIndex: 99999
                        }}
                        className={`bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in duration-150 ${
                            popoverStyle.dropUp ? 'slide-in-from-bottom-2' : 'slide-in-from-top-2'
                        }`}
                    >
                        {searchable && (
                            <div className="p-2.5 bg-white border-b border-slate-100 z-10">
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder={searchPlaceholder}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-8 pr-3 py-2 bg-slate-50 rounded-xl text-xs border border-slate-200 focus:outline-none focus:border-[#067978] focus:bg-white text-slate-700 font-medium"
                                        onClick={(e) => e.stopPropagation()}
                                        autoFocus
                                    />
                                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                </div>
                            </div>
                        )}

                        <div className="max-h-56 overflow-y-auto p-1.5 space-y-1 bg-white scroll-smooth">
                            {filteredOptions.length === 0 ? (
                                <div className="py-4 text-center text-xs text-slate-400 font-medium">
                                    No options found
                                </div>
                            ) : (
                                filteredOptions.map((opt) => {
                                    const optValue = getOptionValue(opt);
                                    const optLabel = getOptionLabel(opt);
                                    const isSelected = String(optValue) === String(value);

                                    return (
                                        <button
                                            key={String(optValue)}
                                            ref={isSelected ? selectedItemRef : null}
                                            type="button"
                                            onClick={() => handleSelect(opt)}
                                            className={`w-full px-3 py-2.5 rounded-xl text-xs font-medium flex items-center justify-between transition-all text-left cursor-pointer ${
                                                isSelected
                                                    ? 'bg-[#067978]/10 text-[#067978] font-semibold'
                                                    : 'text-slate-700 hover:bg-slate-50 hover:text-[#067978]'
                                            }`}
                                        >
                                            <span>{optLabel}</span>
                                            {isSelected && <Check size={14} className="text-[#067978] stroke-[2.5]" />}
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>,
                    document.body
                )}
            </div>
        </div>
    );
};

export default CustomSelect;
